import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api } from '../api';

// Styles définis en dehors pour éviter la recréation à chaque render
const s = {
  card: { background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '1rem' },
  input: { width: '100%', background: 'rgba(51,65,85,0.5)', border: '1px solid #475569', borderRadius: '0.5rem', padding: '0.75rem', color: 'white', outline: 'none', boxSizing: 'border-box', fontSize: '0.9rem', marginBottom: '0.75rem' },
  label: { display: 'block', color: '#94a3b8', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.3rem' },
  btn: { background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.75rem 2rem', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '1rem' },
};

// Défini en dehors de Profil pour éviter le remontage à chaque frappe
const Field = ({ label, field, type = 'text', placeholder = '', value, onChange }) => (
  <div>
    <label style={s.label}>{label}</label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={e => onChange(field, e.target.value)}
      style={s.input}
    />
  </div>
);

const EyeIcon = ({ open }) => open
  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;

const PasswordField = ({ label, name, value, onChange, show, onToggle }) => (
  <div style={{ position: 'relative' }}>
    <label style={s.label}>{label}</label>
    <div style={{ position: 'relative' }}>
      <input
        type={show ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={e => onChange(name, e.target.value)}
        style={{ ...s.input, paddingRight: '2.75rem', marginBottom: 0 }}
        autoComplete="new-password"
      />
      <button
        type="button"
        onClick={onToggle}
        style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
      >
        <EyeIcon open={show} />
      </button>
    </div>
  </div>
);

const Profil = ({ user }) => {
  const [artisan, setArtisan] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwShow, setPwShow] = useState({ currentPassword: false, newPassword: false, confirm: false });
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    // Catch l'erreur si le profil artisan n'existe pas encore
    api.getArtisan()
      .then(d => setArtisan(d || {}))
      .catch(() => setArtisan({}));
  }, []);

  const handleChange = (field, value) => {
    setArtisan(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.updateArtisan(artisan);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const handlePwChange = (field, value) => setPwForm(p => ({ ...p, [field]: value }));
  const toggleShow = (field) => setPwShow(p => ({ ...p, [field]: !p[field] }));

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (pwForm.newPassword.length < 8) {
      toast.error('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }
    setPwLoading(true);
    try {
      await api.changePassword(pwForm.currentPassword, pwForm.newPassword);
      toast.success('Mot de passe mis à jour');
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (e) {
      toast.error(e.message || 'Erreur lors du changement de mot de passe');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ color: 'white', margin: 0 }}>🏢 Mon Profil</h2>
          <p style={{ color: '#64748b', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
            Numéro client : <span style={{ color: '#60a5fa', fontWeight: '700' }}>{artisan.numero_ako || '—'}</span>
            {' · '}Abonnement : <span style={{ color: '#34d399', fontWeight: '600' }}>{artisan.abonnement || '—'}</span>
          </p>
        </div>
        <button onClick={handleSave} disabled={saving} style={{ ...s.btn, opacity: saving ? 0.7 : 1, minHeight: '44px' }}>
          {saving ? '⏳ Sauvegarde...' : saved ? '✅ Sauvegardé !' : '💾 Sauvegarder'}
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.5rem', padding: '0.75rem', color: '#fca5a5', marginBottom: '1rem' }}>
          ❌ {error}
        </div>
      )}

      {/* Informations entreprise */}
      <div style={s.card}>
        <h3 style={{ color: 'white', marginTop: 0, marginBottom: '1rem' }}>📋 Informations entreprise</h3>
        <div style={s.grid}>
          <Field label="Nom de l'entreprise" field="nom_entreprise" placeholder="SARL Mon Entreprise" value={artisan.nom_entreprise || ''} onChange={handleChange} />
          <Field label="SIRET" field="siret" placeholder="123 456 789 00012" value={artisan.siret || ''} onChange={handleChange} />
          <div>
            <label style={s.label}>Forme juridique</label>
            <select
              value={artisan.formeJuridique || artisan.forme_juridique || ''}
              onChange={e => handleChange('formeJuridique', e.target.value)}
              style={{ ...s.input, appearance: 'none' }}
            >
              <option value="">— Sélectionner —</option>
              <option value="auto_entrepreneur">Auto-entrepreneur / Micro-entreprise</option>
              <option value="ei">Entreprise Individuelle (EI)</option>
              <option value="eurl">EURL</option>
              <option value="sarl">SARL</option>
              <option value="sas">SAS</option>
              <option value="sasu">SASU</option>
              <option value="sa">SA</option>
              <option value="autre">Autre</option>
            </select>
          </div>
          <div>
            <label style={s.label}>Régime TVA</label>
            <select
              value={artisan.tvaRegime || artisan.tva_regime || 'franchise_base'}
              onChange={e => handleChange('tvaRegime', e.target.value)}
              style={{ ...s.input, appearance: 'none' }}
            >
              <option value="franchise_base">Franchise en base (art. 293 B — pas de TVA)</option>
              <option value="reel_simplifie">Régime réel simplifié</option>
              <option value="reel_normal">Régime réel normal</option>
            </select>
          </div>
          <Field label="Numéro TVA intracommunautaire" field="tva_number" placeholder="FR12345678901" value={artisan.tva_number || ''} onChange={handleChange} />
          <Field label="Email professionnel" field="email_pro" type="email" placeholder="contact@entreprise.fr" value={artisan.email_pro || ''} onChange={handleChange} />
          <Field label="Téléphone" field="telephone" placeholder="+33 6 00 00 00 00" value={artisan.telephone || ''} onChange={handleChange} />
        </div>
      </div>

      {/* Adresse */}
      <div style={s.card}>
        <h3 style={{ color: 'white', marginTop: 0, marginBottom: '1rem' }}>📍 Adresse</h3>
        <div style={s.grid}>
          <Field label="Adresse" field="adresse" placeholder="12 rue des Lilas" value={artisan.adresse || ''} onChange={handleChange} />
          <Field label="Code postal" field="code_postal" placeholder="75015" value={artisan.code_postal || ''} onChange={handleChange} />
          <Field label="Ville" field="ville" placeholder="Paris" value={artisan.ville || ''} onChange={handleChange} />
        </div>
      </div>

      {/* Coordonnées bancaires */}
      <div style={s.card}>
        <h3 style={{ color: 'white', marginTop: 0, marginBottom: '1rem' }}>🏦 Coordonnées bancaires</h3>
        <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '1rem' }}>
          Ces informations apparaîtront sur vos factures.
        </p>
        <div style={s.grid}>
          <Field label="IBAN" field="iban" placeholder="FR76 3000 4020 0100 0100 1234 567" value={artisan.iban || ''} onChange={handleChange} />
          <Field label="BIC" field="bic" placeholder="BNPAFRPPXXX" value={artisan.bic || ''} onChange={handleChange} />
        </div>
      </div>

      {/* Changement de mot de passe */}
      <div style={s.card}>
        <h3 style={{ color: 'white', marginTop: 0, marginBottom: '0.5rem' }}>🔒 Changer le mot de passe</h3>
        <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '1.25rem' }}>Minimum 8 caractères.</p>
        <form onSubmit={handlePasswordSubmit} autoComplete="off">
          <div style={s.grid}>
            <PasswordField label="Mot de passe actuel" name="currentPassword" value={pwForm.currentPassword} onChange={handlePwChange} show={pwShow.currentPassword} onToggle={() => toggleShow('currentPassword')} />
            <PasswordField label="Nouveau mot de passe" name="newPassword" value={pwForm.newPassword} onChange={handlePwChange} show={pwShow.newPassword} onToggle={() => toggleShow('newPassword')} />
            <PasswordField label="Confirmer le nouveau mot de passe" name="confirm" value={pwForm.confirm} onChange={handlePwChange} show={pwShow.confirm} onToggle={() => toggleShow('confirm')} />
          </div>
          {pwForm.newPassword && pwForm.confirm && pwForm.newPassword !== pwForm.confirm && (
            <p style={{ color: '#f87171', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>Les mots de passe ne correspondent pas</p>
          )}
          <div style={{ marginTop: '1.25rem' }}>
            <button
              type="submit"
              disabled={pwLoading || !pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirm}
              style={{ background: pwLoading ? 'rgba(51,65,85,0.8)' : 'linear-gradient(135deg,#6366f1,#4f46e5)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.75rem 2rem', cursor: (pwLoading || !pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirm) ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.95rem', opacity: (!pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirm) ? 0.5 : 1, transition: 'all 0.2s' }}
            >
              {pwLoading ? '⏳ Mise à jour...' : '🔑 Mettre à jour le mot de passe'}
            </button>
          </div>
        </form>
      </div>

      {/* Infos compte (lecture seule) */}
      <div style={s.card}>
        <h3 style={{ color: 'white', marginTop: 0, marginBottom: '1rem' }}>👤 Informations compte</h3>
        <div style={s.grid}>
          <div>
            <label style={s.label}>Nom</label>
            <input value={user.name || ''} disabled style={{ ...s.input, opacity: 0.6, cursor: 'not-allowed' }} />
          </div>
          <div>
            <label style={s.label}>Email</label>
            <input value={user.email || ''} disabled style={{ ...s.input, opacity: 0.6, cursor: 'not-allowed' }} />
          </div>
          <div>
            <label style={s.label}>Numéro AKO</label>
            <input value={artisan.numero_ako || ''} disabled style={{ ...s.input, opacity: 0.6, cursor: 'not-allowed', color: '#60a5fa', fontWeight: '700' }} />
          </div>
          <div>
            <label style={s.label}>Abonnement</label>
            <input value={artisan.abonnement || ''} disabled style={{ ...s.input, opacity: 0.6, cursor: 'not-allowed', color: '#34d399' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profil;
